import { Suspense } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="bg-white min-h-screen">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center">
              <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-6">
                About Security Bay Area
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Protecting homes and businesses across the Bay Area since 2010 with cutting-edge security solutions
              </p>
            </div>
          </div>
        </section>

        {/* Company Story Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block px-4 py-2 bg-coral-50 rounded-full mb-6">
                  <span className="text-coral-600 font-semibold text-sm">OUR STORY</span>
                </div>
                <h2 className="font-serif text-4xl font-bold text-slate-900 mb-6">
                  Your Trusted Security Partner Since 2010
                </h2>
                <p className="text-lg text-slate-600 mb-6">
                  Founded in San Francisco, Security Bay Area has become the go-to security provider for homes and businesses throughout the Bay Area and surrounding regions. Our journey began with a clear mission: make enterprise-grade security systems powerful yet simple to use for everyone.
                </p>
                <p className="text-lg text-slate-600 mb-6">
                  Today, we proudly serve over 2,000 clients across all industries—from family homes to large commercial facilities. Our success comes from three key strengths: cutting-edge technology, expert installations, and unwavering customer support.
                </p>
                <p className="text-lg text-slate-600 mb-6">
                  With over 40 years of combined experience on our team, we bring deep expertise to every project. We've grown strategically through mergers and acquisitions with trusted local security providers, expanding our reach while maintaining the personalized service our clients expect.
                </p>
                <p className="text-lg text-slate-600">
                  As security technology advances, we stay ahead of the curve. We continuously invest in training, certifications, and partnerships with leading manufacturers to ensure you always have access to the most advanced protection available.
                </p>
              </div>
              <div className="relative">
                <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://public.readdy.ai/ai/img_res/0719a48fdc071004f24ef59dbcb91dd2.jpg"
                    alt="Security Bay Area Team"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Values Section */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-coral-50 rounded-full mb-6">
                <span className="text-coral-600 font-semibold text-sm">OUR VALUES</span>
              </div>
              <h2 className="font-serif text-4xl font-bold text-slate-900 mb-4">
                What Drives Us Forward
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Our core values guide every decision we make and every system we install
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-shield-check-line text-3xl text-coral-600"></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">
                  Security First
                </h3>
                <p className="text-slate-600">
                  We never compromise on security. Every system we design and install meets the highest industry standards and is thoroughly tested to ensure maximum protection for our clients.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-customer-service-2-line text-3xl text-coral-600"></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">
                  Customer Commitment
                </h3>
                <p className="text-slate-600">
                  Our relationship doesn't end at installation. We provide ongoing support, maintenance, and upgrades to ensure your security system continues to protect you for years to come.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-lightbulb-line text-3xl text-coral-600"></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">
                  Innovation
                </h3>
                <p className="text-slate-600">
                  We stay ahead of emerging threats by continuously adopting the latest security technologies, from AI-powered analytics to cloud-based monitoring and smart automation.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-team-line text-3xl text-coral-600"></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">
                  Expert Team
                </h3>
                <p className="text-slate-600">
                  Our certified technicians undergo continuous training and hold industry certifications from leading manufacturers, ensuring expert installation and support every time.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-hand-heart-line text-3xl text-coral-600"></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">
                  Integrity
                </h3>
                <p className="text-slate-600">
                  We provide honest assessments, transparent pricing, and realistic expectations. We'll never oversell you on features you don't need or make promises we can't keep.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-leaf-line text-3xl text-coral-600"></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">
                  Sustainability
                </h3>
                <p className="text-slate-600">
                  We prioritize energy-efficient systems and sustainable practices, helping our clients reduce their environmental impact while maintaining top-tier security.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-slate-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-coral-500 mb-2">2,000+</div>
                <div className="text-slate-300 text-lg">Clients Protected</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-coral-500 mb-2">15+</div>
                <div className="text-slate-300 text-lg">Years Experience</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-coral-500 mb-2">20+</div>
                <div className="text-slate-300 text-lg">Certified Technicians</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-coral-500 mb-2">40+</div>
                <div className="text-slate-300 text-lg">Years Combined Experience</div>
              </div>
            </div>
          </div>
        </section>

        {/* Certifications Section */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-coral-50 rounded-full mb-6">
                <span className="text-coral-600 font-semibold text-sm">CERTIFICATIONS</span>
              </div>
              <h2 className="font-serif text-4xl font-bold text-slate-900 mb-4">
                Industry Recognition
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Our certifications and partnerships demonstrate our commitment to excellence
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              <div className="bg-white rounded-xl p-8 text-center shadow-lg">
                <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-verified-badge-line text-3xl text-coral-600"></i>
                </div>
                <h3 className="font-semibold text-slate-900 mb-2 text-lg">UL Listed</h3>
                <p className="text-sm text-slate-600">Underwriters Laboratories Certified</p>
              </div>

              <div className="bg-white rounded-xl p-8 text-center shadow-lg">
                <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-star-line text-3xl text-coral-600"></i>
                </div>
                <h3 className="font-semibold text-slate-900 mb-2 text-lg">ACO Licensed</h3>
                <p className="text-sm text-slate-600">Alarm Company Operator License</p>
              </div>

              <div className="bg-white rounded-xl p-8 text-center shadow-lg">
                <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-award-line text-3xl text-coral-600"></i>
                </div>
                <h3 className="font-semibold text-slate-900 mb-2 text-lg">CSLB Licensed</h3>
                <p className="text-sm text-slate-600">Contractors State Licensed Board Licensed</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-slate-900">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Protect What Matters Most?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Let's discuss how Security Bay Area can design the perfect security solution for your needs
            </p>
            <a
              href="/contact-us"
              className="inline-flex items-center px-8 py-4 bg-coral-600 text-white rounded-full font-semibold text-lg hover:bg-coral-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Schedule a Consultation
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
